import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import { Helmet } from 'react-helmet';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const MassPercentCalculator = () => {
  const [massData, setMassData] = useState({
    massOfSolute: '',
    massOfSolution: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMassData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateMassPercent = () => {
    const { massOfSolute, massOfSolution } = massData;
    
    const soluteMass = parseFloat(massOfSolute) || 0;
    const solutionMass = parseFloat(massOfSolution) || 0;

    if (soluteMass === 0 || isNaN(soluteMass)) {
      setError('Please enter a valid mass of solute');
      return;
    }

    if (solutionMass === 0 || isNaN(solutionMass)) {
      setError('Please enter a valid mass of solution');
      return;
    }

    // Calculate mass percent
    const massPercent = (soluteMass / solutionMass) * 100;

    const calculationSteps = [
      'Step 1: Prepare the values',
      `Mass of solute: ${soluteMass} g`,
      `Mass of solution: ${solutionMass} g`,
      '\nStep 2: Calculate mass percent',
      `Mass percent = (Mass of solute / Mass of solution) * 100`,
      `Mass percent = (${soluteMass} / ${solutionMass}) * 100 = ${massPercent.toFixed(2)} %`
    ];

    const newResults = {
      massPercent: massPercent.toFixed(2),
      calculationSteps
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['Mass Percent'],
        datasets: [{
          label: 'Values',
          data: [massPercent],
          backgroundColor: [
            'rgba(75, 192, 192, 0.5)'
          ],
          borderColor: [
            'rgba(75, 192, 192, 1)'
          ],
          borderWidth: 1
        }]
      }
    });
  };

  const exampleData = {
    massOfSolute: 10,
    massOfSolution: 100,
    massPercent: 10.00
  };

  return (
    <>
     <Helmet>
      <title>Mass Percent Calculator - Solution Concentration Analysis</title>
      <meta name="description" content="Calculate solution mass percent using mass of solute and mass of solution. Understand solution concentration with our interactive mass percent calculator." />
      <meta name="keywords" content="mass percent calculator, solution concentration, chemistry calculator, solution preparation, chemistry education, laboratory work" />
      <meta name="author" content="Your Company Name" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta property="og:title" content="Mass Percent Calculator - Solution Concentration Analysis" />
      <meta property="og:description" content="Calculate solution mass percent using mass of solute and mass of solution. Understand solution concentration with our interactive mass percent calculator." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://yourdomain.com/mass-percent-calculator" />
      <meta property="og:image" content="https://yourdomain.com/mass-percent-calculator-preview.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:site_name" content="Chemistry Calculators" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@your_twitter_handle" />
      <meta name="twitter:creator" content="@your_twitter_handle" />
      <meta name="twitter:title" content="Mass Percent Calculator - Solution Concentration Analysis" />
      <meta name="twitter:description" content="Calculate solution mass percent using mass of solute and mass of solution. Understand solution concentration with our interactive mass percent calculator." />
      <meta name="twitter:image" content="https://yourdomain.com/mass-percent-calculator-preview.jpg" />
      <meta name="twitter:image:alt" content="Mass Percent Calculator Preview" />
      <meta name="pinterest" content="nopin" />
      <meta name="pinterest-rich-pin" content="true" />
      <meta itemprop="name" content="Mass Percent Calculator - Solution Concentration Analysis" />
      <meta itemprop="description" content="Calculate solution mass percent using mass of solute and mass of solution. Understand solution concentration with our interactive mass percent calculator." />
      <meta itemprop="image" content="https://yourdomain.com/mass-percent-calculator-preview.jpg" />
      <meta name="theme-color" content="#ffffff" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Mass Percent Calculator" />
    </Helmet>

    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🧪</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Mass Percent Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Solution Concentration Analysis</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate the mass percent of a solution using the mass of solute and mass of solution. 
          This calculator helps you understand solution concentration concepts.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="massOfSolute">
            Mass of Solute (g):
          </label>
          <input
            type="number"
            id="massOfSolute"
            name="massOfSolute"
            value={massData.massOfSolute}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter mass of solute in grams"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="massOfSolution">
            Mass of Solution (g):
          </label>
          <input
            type="number"
            id="massOfSolution"
            name="massOfSolution"
            value={massData.massOfSolution}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter mass of solution in grams"
          />
        </div>

        <button
          onClick={calculateMassPercent}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Mass Percent
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Solution Properties</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Mass Percent (%)</span>
                    <span className="font-medium">{results.massPercent}</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Value'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Solution Property'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Solution Properties'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Example Calculation <span className="ml-2 text-2xl">📝</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Here's an example calculation using a sugar solution:
          </p>
          
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold mb-4">Sample Data:</h4>
            <ul className="list-disc pl-8 space-y-2">
              <li>Mass of solute (sugar): {exampleData.massOfSolute} g</li>
              <li>Mass of solution (water + sugar): {exampleData.massOfSolution} g</li>
            </ul>
            
            <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
            <ol className="list-decimal pl-8 space-y-4">
              <li>
                <p>Calculate mass percent:</p>
                <ul className="list-none pl-8 space-y-2">
                  <li>Mass percent = (Mass of solute / Mass of solution) * 100</li>
                  <li>Mass percent = ({exampleData.massOfSolute} / {exampleData.massOfSolution}) * 100 = {exampleData.massPercent} %</li>
                </ul>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Mass Percent Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Mass Percent Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Gather Solution Data</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Measure the mass of solute accurately</li>
                <li>Measure the mass of solution</li>
                <li>Ensure all measurements are precise</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Enter Solution Information</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Input the mass of solute in grams</li>
                <li>Enter the mass of solution in grams</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the calculated mass percent</li>
                <li>Use the bar chart for visual analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-6">
            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What is mass percent?</h3>
              <p className="text-gray-600">
                Mass percent (also known as mass/weight percent) is a measure of the concentration of a component in a mixture. 
                It is calculated as the mass of the component divided by the total mass of the mixture, multiplied by 100.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why is mass percent important in chemistry?</h3>
              <p className="text-gray-600">
                Mass percent is important in chemistry because it:
                <ul className="list-disc pl-6 mt-2">
                  <li>Provides a way to express concentration without needing to know the volume</li>
                  <li>Is useful for stoichiometric calculations in reactions</li>
                  <li>Helps in preparing solutions with precise concentrations</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">How do I convert between mass percent and other concentration units?</h3>
              <p className="text-gray-600">
                Conversion between mass percent and other concentration units (like molarity) involves using the molar mass of the solute and the density of the solution. 
                Specific calculations may vary based on the units involved.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">What if I don't know the total mass of the solution?</h3>
              <p className="text-gray-600">
                You can find the total mass of the solution by summing the masses of all components, or by measuring it directly if possible.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Why are my results different from expected?</h3>
              <p className="text-gray-600">
                If your results seem unexpected, check:
                <ul className="list-disc pl-6 mt-2">
                  <li>If you've entered the correct masses</li>
                  <li>If your measurements are accurate</li>
                  <li>If all values are in the correct units</li>
                </ul>
              </p>
            </div>

            <div className="faq-item">
              <h3 className="text-lg font-semibold mb-2">Is mass percent affected by temperature?</h3>
              <p className="text-gray-600">
                Mass percent is generally not affected by temperature because it is a ratio of masses. However, if the solution's density changes significantly with temperature, it may indirectly affect the calculations.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
            <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold mb-2">Visual Analysis</h3>
              <p className="text-gray-600">Interactive bar charts for easy visualization of solution properties</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Precise Calculations</h3>
              <p className="text-gray-600">Accurate mass percent calculations with step-by-step breakdown</p>
            </div>
            <div className="text-center p-6 bg-yellow-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Unit Conversion</h3>
              <p className="text-gray-600">Automatic handling of mass units</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="text-lg font-semibold mb-2">Educational Value</h3>
              <p className="text-gray-600">Detailed explanations of chemistry concepts</p>
            </div>
            <div className="text-center p-6 bg-pink-50 rounded-lg">
              <div className="text-4xl mb-4">⚙️</div>
              <h3 className="text-lg font-semibold mb-2">User-Friendly</h3>
              <p className="text-gray-600">Intuitive interface with clear input fields</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">🔄</div>
              <h3 className="text-lg font-semibold mb-2">Real-time Updates</h3>
              <p className="text-gray-600">Instant calculation results as you type</p>
            </div>
          </div>

          <div className="mt-12">
            <h3 className="text-xl font-semibold mb-4">Key Benefits</h3>
            <ul className="list-disc pl-8 space-y-2">
              <li>Save time with automatic calculations</li>
              <li>Reduce calculation errors with built-in validation</li>
              <li>Learn chemistry concepts through practical examples</li>
              <li>Accessible from any device</li>
              <li>Free to use for educational and professional purposes</li>
              <li>Perfect for students, teachers, and professionals</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Mass Percent <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Mass percent is a measure of solution concentration that helps in:
          </p>
          <ul className="list-none pl-8 space-y-4">
            <li className="flex items-center">
              <span className="h-3 w-3 bg-blue-600 rounded-full mr-2"></span>
              <span className="font-medium">Chemical Reactions:</span> Determining reactant amounts and product yields
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-green-600 rounded-full mr-2"></span>
              <span className="font-medium">Solution Preparation:</span> Making accurate dilutions and standard solutions
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
              <span className="font-medium">Laboratory Work:</span> Ensuring consistent solution concentrations
            </li>
            <li className="flex items-center">
              <span className="h-3 w-3 bg-red-600 rounded-full mr-2"></span>
              <span className="font-medium">Industrial Processes:</span> Controlling chemical manufacturing processes
            </li>
          </ul>
          <p className="italic text-gray-500 mt-6">
            Note: Mass percent is generally not affected by temperature as it is a ratio of masses. However, if the solution's density changes significantly with temperature, it may indirectly affect the calculations.
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default MassPercentCalculator;